import { owaComputedFn } from "owa-computed-fn";
import { type MailRibbonControlId, type MailRibbonGroupId, type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { RibbonGroupId, RibbonControlId } from "owa-ribbon-ids";
import type { ClientItemId } from "owa-client-ids";
import { getMailRibbonConfigStore } from "owa-mail-ribbon-store/lib/store/store";
import { getDefaultMLRGroupControls } from "./getDefaultMLRGroupControls";
import type { ReadOnlyRibbonControlDefProps } from "./RibbonControlDefinitionProps";
import { type Layout, type VerticalControlStack, sections, singleRow, stack } from "owa-acui";
import { getReadRibbonId } from "./getReadRibbonId";
import { getStringRibbonId } from "owa-ribbon-ids";

export const isMLRGroupCustomized: (groupId: RibbonGroupId, tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => boolean = owaComputedFn(function isMLRGroupCustomized(groupId: RibbonGroupId, tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): boolean {
    const controlIds = getMailRibbonConfigStore().multiline.homeTab.layout.find(g => g.groupId === groupId)?.controlIds;
    const defaultControlIds = getDefaultMLRGroupControls(groupId, tabId, props);
    
    // special handling for the Delete group, since Restore/RestoreAll are not customizable
    // and are prepended to the first group in the layout after the NewMessage group
    if (groupId === 104 && controlIds) {
        const filteredControlIds = controlIds.filter(id => id !== 683 && id !== 684);
        const filteredDefaultControlIds = defaultControlIds.filter(id => id !== 683 && id !== 684);
        return !filteredControlIds.every((id, index) => id === filteredDefaultControlIds[index]);
    }
    
    if (!controlIds || //default group has been removed from the stored ribbon
        controlIds.length !== defaultControlIds.length || // number of controls in the stored group differs from the default
        !controlIds.every((id, index) => id === defaultControlIds[index]) // order of controls differs from the default
    ) {
        return true;
    }
    return false;
});

export const getCustomizedLayoutForGroup: (groupId: RibbonGroupId, props: ReadOnlyRibbonControlDefProps, itemId: ClientItemId | undefined) => Layout[] | undefined = (groupId, props, itemId) => {
    const controlIds = getMailRibbonConfigStore(props.mailboxInfo).multiline.homeTab.layout.find(g => g.groupId === groupId)?.controlIds;
    
    // A single control is always shown as a large button, so no custom layout is needed.
    if (!controlIds || controlIds.length <= 1) {
        return undefined;
    }
    
    const unstackedButtonCount = unstackedButtonCounts[controlIds.length];
    if (unstackedButtonCount) {
        return unstackedButtonCount.map(numLargeButtons => buildLayoutOption(controlIds, itemId, numLargeButtons));
    }
    
    // more than 12 controls: either all unstacked or all stacked
    return [
        buildLayoutOption(controlIds, itemId, controlIds.length),
        sections(singleRow(...stackEveryThree(controlIds, itemId))),
    ];
};

// return layout with the specified number of large buttons, and the rest stacked in groups of 3
const buildLayoutOption = (controlIds: RibbonControlId[], itemId: ClientItemId | undefined, numLargeButtons: number): Layout => {
    const layoutControls = controlIds.map(controlId => getLayoutControl(controlId, itemId));
    const largeButtons = layoutControls.slice(0, numLargeButtons);
    const stacks = stackEveryThree(controlIds.slice(numLargeButtons), itemId);
    return sections(singleRow(...largeButtons, ...stacks));
};

const stackEveryThree = (controlIds: RibbonControlId[], itemId: ClientItemId | undefined): VerticalControlStack[] => {
    const stacks: VerticalControlStack[] = [];
    for (let i = 0; i < controlIds.length; i += 3) {
        const chunk = controlIds.slice(i, i + 3).map(id => getLayoutControl(id, itemId));
        stacks.push(stack(chunk[0], chunk[1] ?? null, chunk[2] ?? null));
    }
    return stacks;
};

// how many unstacked buttons precede the stacked groups for each group size at various screen widths
// <group size> : [<large width count>, <medium width count>, <small width count>]
const unstackedButtonCounts: {
    [key: number]: number[];
} = {
    2: [2, 0],
    3: [3, 0],
    4: [4, 1],
    5: [5, 2],
    6: [3, 0],
    7: [7, 1],
    8: [5, 2],
    9: [9, 0],
    10: [10, 1],
    11: [11, 5, 2],
    12: [4, 1],
};

const getLayoutControl: (controlId: RibbonControlId, itemId: ClientItemId | undefined) => string = owaComputedFn(function getLayoutControl(controlId: RibbonControlId, itemId: ClientItemId | undefined): string {
    switch (controlId) {
        case 519:
        case 657:
        case 559:
        case 556:
        case 557:
        case 555:
        case 678:
        case 686:
        case 712:
        case 552:
        case 527:
        case 616:
        case 549:
        case 697:
        case 731:
        case 709:
            return getReadRibbonId(controlId, itemId).toString();
        default:
            return getStringRibbonId(controlId);
    }
});
